import * as React from "react";
import { WarningBanner } from "@graffer-inc/console-design-kit";
import { useEffect, useId } from "react";
import { reportUnexpectedError } from "../../../../error-reporter";
import { customUIMarkdownProcessorFactory } from "../../../../utils/markdown/processor";

const processor = customUIMarkdownProcessorFactory();

export const MarkdownPageEditor: React.FC<{
  initialMarkdown: string;
  onChange: (markdown: string) => void;
  isShowEditor: boolean;
  isServiceProvider: boolean;
}> = ({ initialMarkdown, onChange, isShowEditor, isServiceProvider }) => {
  const [value, setValue] = React.useState(initialMarkdown);
  const [errMsg, setErrMsg] = React.useState("");
  const [previewElements, setPreviewElements] = React.useState(null);

  const convertImgToDiv = (target: string): string => {
    const html = new DOMParser().parseFromString(target, "text/html");
    const imgList = html.body.querySelectorAll("img");

    for (let i = 0; i < imgList.length; i++) {
      const div = document.createElement("div");
      // use same color as sidebar item background:
      // https://github.com/graffer-inc/graffer-console-design-kit/blob/afaeae60c9250c507806fbbfa1f37a35f65804f1/src/components/console/organisms/menu-list/menuList.module.css#L18
      div.style.backgroundColor = "#f1f2f7";
      div.style.padding = "12px 20px";
      div.style.borderRadius = "14px";
      div.textContent = "プレビューでは画像は表示されません";

      imgList[i].replaceWith(div);
    }

    return html.body.innerHTML;
  };

  useEffect(() => {
    try {
      // LGWANからカスタムページを表示する際、画像ファイルが含まれる場合は
      // 本来無害化処理を施してから表示しなければならない。
      // カスタムページの画像を無害化するための実装・データ移行は大掛かりとなるため、
      // 画像（imgタグ）は表示ができない旨を記載するdivタグに置き換えてファイル無害化が不要なコンテンツに変換する。
      // isServiceProviderでLGWANからのアクセスか否かを判定するのは正確ではないが、
      // テナントユーザーであればLGWANからのアクセスであるとみなして処理する。
      const previewValue = isServiceProvider ? value : convertImgToDiv(value);

      // 型定義の管理的に解決できないのでany化する
      // https://github.com/unifiedjs/unified/issues/88
      setPreviewElements((processor.processSync(previewValue) as any).result);
      setErrMsg("");
    } catch (error) {
      // TODO(twakino): UI上、エラーのフィードバックは表示されない。テナント公開時には要検討。
      reportUnexpectedError(error);
      setPreviewElements(null);
      setErrMsg(error.message);
    }
  }, [value]);

  useEffect(() => {
    setValue(initialMarkdown);
  }, [initialMarkdown]);

  return (
    <div className="columns">
      {isShowEditor && (
        <div className="column is-6">
          <textarea
            className="textarea is-small"
            style={{
              minHeight: "600px",
              maxHeight: "1000px",
            }}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              onChange(e.target.value);
            }}
          />
        </div>
      )}
      <div className="column is-6">
        <div
          style={{
            borderBottom: "1px solid rgba(128,128,128)",
            padding: "8px 0",
            marginBottom: "16px",
          }}
        >
          <label className="label" htmlFor={`preview-${useId()}`}>
            ページプレビュー
          </label>
          {!isShowEditor && (
            <p className="help has-text-weight-normal mb-4">
              設定されたカスタムページのプレビューが表示されます。
            </p>
          )}
        </div>
        {errMsg !== "" && (
          <WarningBanner
            message="プレビューできませんでした"
            description="HTMLを修正してください。"
          />
        )}
        <div className="content">
          <div>{previewElements}</div>
        </div>
      </div>
    </div>
  );
};
